/**
 * Bonus caves, every fifth Expedition depth: one 20 x 12 screen and one idea, as the 1984
 * intermissions were (docs/spec/03 §2). Each theme is a small set of features laid out first,
 * with the exit placed after them wherever there is room.
 */

import type { Rng } from '../../sim/rng';
import { fireflyLoop, magicWall } from './creatures';
import { clocks, expandCorridor, slimeOverMoth } from './deep';
import type { Build, FeatureId, Plan } from './plan';
import { awayFromInbox } from './plan';
import type { Sketch } from './sketch';
import { cache, shelf } from './structures';
import type { PickedFeature } from './table';

/** Three butterflies side by side in dirt pockets under one long shelf of boulders. */
export function mothRow(sk: Sketch, rng: Rng, plan: Plan): boolean {
  const pockets = 3;
  const spot = sk.findSpot(rng, pockets * 3 + 1, 6, awayFromInbox(plan));
  if (!spot) return false;
  sk.claim(spot);
  sk.fillRect(spot, '.');
  for (let x = spot.x + 1; x < spot.x + spot.w - 1; x++) sk.set(x, spot.y + 1, 'r');
  for (let p = 0; p < pockets; p++) {
    const x = spot.x + 1 + p * 3;
    sk.fillRect({ x, y: spot.y + 3, w: 2, h: 2 }, ' ');
    const c = { x: x + rng.int(2), y: spot.y + 3 + rng.int(2) };
    sk.set(c.x, c.y, 'b');
    plan.creatures.push(c);
  }
  return true;
}

export interface BonusTheme {
  /** The first depth it may appear at (Classic themes). */
  readonly from: number;
  readonly deep: boolean;
  readonly features: readonly PickedFeature[];
  /** Extra share of the reachable diamonds the quota asks for. */
  readonly greed: number;
  /** Extra diamond fill, percent. */
  readonly gems: number;
}

function theme(from: number, deep: boolean, greed: number, gems: number, ...parts: [FeatureId, Build][]): BonusTheme {
  return { from, deep, greed, gems, features: parts.map(([id, build]) => ({ id, build })) };
}

const THEMES: readonly BonusTheme[] = [
  // Gold rush: a rich screen, a big quota.
  theme(1, false, 0.15, 4, ['cache', cache], ['shelf', shelf]),
  // Moths in a row under a shelf of boulders.
  theme(3, false, 0, 0, ['butterfly', mothRow]),
  // A sentry circling a store of diamonds.
  theme(2, false, 0, 0, ['firefly', fireflyLoop]),
  // A magic wall race against its timer.
  theme(6, false, 0.05, 0, ['magic', magicWall], ['shelf', shelf]),
  // Slime over a butterfly.
  theme(1, true, 0, 0, ['slime', slimeOverMoth]),
  // Expanding walls: a one-screen sprint.
  theme(1, true, 0, 2, ['expand', expandCorridor], ['expand', expandCorridor]),
  // Clocks as the prize.
  theme(1, true, 0.1, 2, ['clocks', clocks], ['cache', cache]),
];

/** A theme for this depth: with the Deep on, a Deep one three times in four, so the cave looks the part. */
export function pickBonus(rng: Rng, depth: number, deep: boolean): BonusTheme {
  const wantDeep = deep && !rng.oneIn(4);
  const open = THEMES.filter((t) => (wantDeep ? t.deep : !t.deep && depth >= t.from));
  return open[rng.int(open.length)] ?? THEMES[0] ?? { from: 1, deep: false, greed: 0, gems: 0, features: [] };
}
